import React from 'react';
import { Eye, Brain, Zap, BarChart2, Activity } from 'lucide-react';
export const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ?? Primitive SVG building blocks ?????????????????????????????????????????

export const C = ({ x, o, c, h, l, w = 12 }) => {
  const bull = c <= o; // SVG y-axis is inverted (lower y = higher price)
  const col  = bull ? '#16C784' : '#ef4444';
  const bTop = Math.min(o, c);
  const bH   = Math.max(Math.abs(c - o), 2);
  return (
    <g>
      <line x1={x + w / 2} y1={h} x2={x + w / 2} y2={l} stroke={col} strokeWidth="1.5" />
      <rect x={x} y={bTop} width={w} height={bH} fill={col} rx="1" />
    </g>
  );
};

export const HL = ({ y, x1 = 5, x2 = 195, color = '#94a3b8', dash = false }) => (
  <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth="1.5"
    strokeDasharray={dash ? '5,3' : 'none'} />
);

export const Lbl = ({ x, y, text, color = '#94a3b8', anchor = 'middle', size = 8 }) => (
  <text x={x} y={y} fill={color} fontSize={size} textAnchor={anchor}
    fontFamily="ui-monospace,monospace">{text}</text>
);

export const Div = () => (
  <line x1="100" y1="5" x2="100" y2="105" stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
);

// ?? Card visuals (one SVG per card) ???????????????????????????????????????

export const V = {
  single_candle: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={85} o={78} c={28} h={12} l={95} w={30} />
      <HL y={12} x1={60} x2={85} color="#94a3b8" dash />
      <Lbl x={58} y={15} text="H" anchor="end" />
      <HL y={28} x1={60} x2={85} color="#16C784" dash />
      <Lbl x={58} y={31} text="C" anchor="end" color="#16C784" />
      <HL y={78} x1={60} x2={85} color="#16C784" dash />
      <Lbl x={58} y={81} text="O" anchor="end" color="#16C784" />
      <HL y={95} x1={60} x2={85} color="#94a3b8" dash />
      <Lbl x={58} y={98} text="L" anchor="end" />
      <text x="125" y="38" fill="#16C784" fontSize="9" fontFamily="sans-serif">? Buyers pushed</text>
      <text x="125" y="78" fill="#ef4444" fontSize="9" fontFamily="sans-serif">? Sellers pushed</text>
    </svg>
  ),

  body_wick: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={28} o={75} c={22} h={15} l={82} w={32} />
      <Lbl x={44} y={105} text="Strength" color="#16C784" />
      <C x={128} o={58} c={52} h={8} l={100} w={32} />
      <Lbl x={144} y={105} text="Rejection" color="#ef4444" />
    </svg>
  ),

  size_matters: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={10} o={55} c={50} h={47} l={58} w={13} />
      <C x={26} o={52} c={57} h={50} l={60} w={13} />
      <C x={42} o={55} c={50} h={47} l={58} w={13} />
      <text x="65" y="57" fill="#94a3b8" fontSize="16">?</text>
      <C x={88} o={82} c={15} h={10} l={86} w={32} />
      <Lbl x={36} y={105} text="Indecision" color="#94a3b8" />
      <Lbl x={104} y={105} text="Momentum" color="#16C784" />
    </svg>
  ),

  speed: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={5}  o={85} c={78} h={76} l={87} w={10} />
      <C x={18} o={78} c={85} h={82} l={88} w={10} />
      <C x={31} o={82} c={76} h={74} l={84} w={10} />
      <C x={44} o={77} c={70} h={68} l={79} w={10} />
      <Lbl x={35} y={105} text="Slow / Weak" color="#94a3b8" />
      <C x={110} o={88} c={72} h={69} l={90} w={10} />
      <C x={123} o={72} c={52} h={49} l={74} w={10} />
      <C x={136} o={52} c={30} h={27} l={54} w={10} />
      <C x={149} o={30} c={12} h={9}  l={32} w={10} />
      <Lbl x={145} y={105} text="Fast / Strong" color="#16C784" />
    </svg>
  ),

  trend_sideways: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={5}  o={85} c={73} h={70} l={87} w={10} />
      <C x={18} o={75} c={85} h={82} l={88} w={10} />
      <C x={31} o={68} c={56} h={53} l={70} w={10} />
      <C x={44} o={58} c={68} h={65} l={71} w={10} />
      <C x={57} o={52} c={40} h={37} l={54} w={10} />
      <Lbl x={38} y={105} text="Trend" color="#16C784" />
      <C x={108} o={55} c={48} h={44} l={59} w={10} />
      <C x={121} o={50} c={57} h={54} l={62} w={10} />
      <C x={134} o={54} c={48} h={44} l={58} w={10} />
      <C x={147} o={51} c={58} h={55} l={63} w={10} />
      <C x={160} o={55} c={49} h={45} l={60} w={10} />
      <Lbl x={142} y={105} text="Sideways / Noise" color="#94a3b8" />
    </svg>
  ),

  hh_hl: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <polyline points="15,85 45,52 75,65 115,35 148,48 180,18"
        fill="none" stroke="#16C784" strokeWidth="2" />
      {[{cx:45,cy:52},{cx:115,cy:35},{cx:180,cy:18}].map((p,i)=>(
        <g key={i}>
          <circle cx={p.cx} cy={p.cy} r="3.5" fill="#16C784" />
          <text x={p.cx} y={p.cy-7} fill="#16C784" fontSize="8" textAnchor="middle">HH</text>
        </g>
      ))}
      {[{cx:15,cy:85},{cx:75,cy:65},{cx:148,cy:48}].map((p,i)=>(
        <g key={i}>
          <circle cx={p.cx} cy={p.cy} r="3.5" fill="#6366F1" />
          <text x={p.cx} y={p.cy+12} fill="#6366F1" fontSize="8" textAnchor="middle">HL</text>
        </g>
      ))}
    </svg>
  ),

  breakout: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={38} color="#ef4444" dash />
      <Lbl x={190} y={35} text="Resistance" color="#ef4444" anchor="end" />
      <C x={10} o={65} c={58} h={55} l={68} w={11} />
      <C x={24} o={60} c={67} h={64} l={72} w={11} />
      <C x={38} o={65} c={59} h={56} l={68} w={11} />
      <C x={52} o={62} c={68} h={65} l={72} w={11} />
      <C x={66} o={66} c={60} h={57} l={69} w={11} />
      <C x={84} o={65} c={15} h={10} l={68} w={22} />
      <text x="135" y="25" fill="#16C784" fontSize="18">?</text>
      <Lbl x={155} y={20} text="Breakout" color="#16C784" />
    </svg>
  ),

  rejection: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={18} color="#ef4444" dash />
      <Lbl x={7} y={15} text="Supply" color="#ef4444" anchor="start" />
      <C x={18} o={75} c={65} h={62} l={78} w={12} />
      <C x={33} o={67} c={57} h={54} l={70} w={12} />
      {/* Rejection candle ? small body, very long upper wick */}
      <C x={52} o={72} c={68} h={8}  l={76} w={14} />
      <text x="90" y="12" fill="#ef4444" fontSize="8" fontFamily="sans-serif">? Rejection</text>
      <C x={72} o={66} c={78} h={75} l={82} w={12} />
      <C x={87} o={80} c={92} h={88} l={95} w={12} />
    </svg>
  ),

  sr_bounces: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={18} color="#ef4444" dash />
      <Lbl x={7} y={15} text="Resistance" color="#ef4444" anchor="start" size={7} />
      <HL y={88} color="#16C784" dash />
      <Lbl x={7} y={102} text="Support" color="#16C784" anchor="start" size={7} />
      <polyline points="15,88 45,22 78,88 118,22 158,88 185,28"
        fill="none" stroke="#6366F1" strokeWidth="1.5" />
      {[{cx:15,cy:88,c:'#16C784'},{cx:45,cy:22,c:'#ef4444'},{cx:78,cy:88,c:'#16C784'},
        {cx:118,cy:22,c:'#ef4444'},{cx:158,cy:88,c:'#16C784'}].map((p,i)=>(
        <circle key={i} cx={p.cx} cy={p.cy} r="3.5" fill={p.c} />
      ))}
    </svg>
  ),

  volume_confirm: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={28} o={68} c={42} h={39} l={70} w={22} />
      <rect x={28} y={82} width={22} height={8} fill="#16C784" opacity="0.35" rx="1" />
      <Lbl x={39} y={105} text="Weak vol ? fails" color="#ef4444" />
      <C x={128} o={72} c={18} h={15} l={74} w={22} />
      <rect x={128} y={68} width={22} height={22} fill="#16C784" opacity="0.7" rx="1" />
      <Lbl x={139} y={105} text="Strong vol ? holds" color="#16C784" />
    </svg>
  ),

  strength_weakness: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={5}  o={88} c={74} h={72} l={90} w={12} />
      <C x={20} o={74} c={60} h={58} l={76} w={12} />
      <C x={35} o={60} c={44} h={42} l={62} w={12} />
      <C x={50} o={44} c={28} h={26} l={46} w={12} />
      <Lbl x={38} y={105} text="Strong momentum" color="#16C784" />
      <C x={110} o={55} c={48} h={43} l={60} w={12} />
      <C x={125} o={50} c={57} h={54} l={65} w={12} />
      <C x={140} o={54} c={48} h={43} l={59} w={12} />
      <C x={155} o={51} c={58} h={55} l={66} w={12} />
      <C x={170} o={55} c={49} h={44} l={61} w={12} />
      <Lbl x={148} y={105} text="Weak / Choppy" color="#94a3b8" />
    </svg>
  ),

  pullback: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={5}  o={85} c={73} h={70} l={87} w={11} />
      <C x={19} o={73} c={60} h={57} l={75} w={11} />
      <C x={33} o={60} c={48} h={45} l={62} w={11} />
      {/* pullback red */}
      <C x={48} o={48} c={58} h={55} l={62} w={11} />
      <C x={62} o={58} c={68} h={65} l={72} w={11} />
      {/* continuation */}
      <C x={80} o={66} c={52} h={49} l={68} w={11} />
      <C x={94} o={52} c={38} h={35} l={54} w={11} />
      <C x={108} o={38} c={23} h={20} l={40} w={11} />
      <Lbl x={22} y={105} text="Trend" color="#16C784" />
      <Lbl x={60} y={105} text="Pullback" color="#ef4444" />
      <Lbl x={100} y={105} text="Continue" color="#16C784" />
    </svg>
  ),

  reversal: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={5}  o={22} c={35} h={20} l={37} w={11} />
      <C x={19} o={35} c={48} h={32} l={50} w={11} />
      <C x={33} o={48} c={62} h={45} l={64} w={11} />
      <HL y={82} x1={44} x2={80} color="#16C784" dash />
      {/* reversal ? long lower wick */}
      <C x={50} o={74} c={70} h={66} l={92} w={13} />
      <text x="80" y="76" fill="#16C784" fontSize="8" fontFamily="sans-serif">Reversal signal</text>
      <C x={70} o={67} c={55} h={52} l={69} w={11} />
      <C x={84} o={55} c={42} h={39} l={57} w={11} />
      <C x={98} o={42} c={28} h={25} l={44} w={11} />
      <text x="148" y="50" fill="#16C784" fontSize="12">?</text>
      <Lbl x={160} y={45} text="Shift" color="#16C784" />
    </svg>
  ),

  do_nothing: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={5}  o={52} c={44} h={28} l={74} w={12} />
      <C x={20} o={48} c={57} h={33} l={78} w={12} />
      <C x={35} o={55} c={47} h={30} l={72} w={12} />
      <C x={50} o={50} c={60} h={36} l={80} w={12} />
      <C x={65} o={57} c={47} h={26} l={76} w={12} />
      <C x={80} o={49} c={58} h={32} l={75} w={12} />
      <C x={95} o={54} c={45} h={28} l={74} w={12} />
      <rect x={2} y={2} width={112} height={104} fill="#ef4444" opacity="0.05" rx="4" />
      <text x="58" y="18" fill="#ef4444" fontSize="9" textAnchor="middle" fontFamily="sans-serif">
        No Trade Zone
      </text>
    </svg>
  ),

  // ?? THINK visuals ???????????????????????????????????????????????????????
  three_checks: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={5}  o={82} c={68} h={65} l={84} w={10} />
      <C x={18} o={68} c={54} h={51} l={70} w={10} />
      <C x={31} o={54} c={40} h={37} l={56} w={10} />
      <Lbl x={27} y={105} text="Trend ?" color="#16C784" />
      <line x1="58" y1="5" x2="58" y2="105" stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
      <HL y={42} x1={62} x2={100} color="#ef4444" dash />
      <C x={63} o={62} c={57} h={54} l={65} w={10} />
      <C x={76} o={59} c={64} h={61} l={68} w={10} />
      <Lbl x={80} y={105} text="Event ?" color="#f59e0b" />
      <line x1="105" y1="5" x2="105" y2="105" stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
      <C x={110} o={64} c={22} h={17} l={67} w={22} />
      <Lbl x={126} y={105} text="Confirm ?" color="#16C784" />
    </svg>
  ),

  no_confirm: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={35} color="#ef4444" dash />
      <Lbl x={190} y={32} text="Resistance" color="#ef4444" anchor="end" />
      <C x={40} o={58} c={52} h={49} l={61} w={11} />
      <C x={54} o={53} c={48} h={25} l={57} w={11} />
      <C x={68} o={52} c={62} h={59} l={66} w={11} />
      <C x={82} o={62} c={74} h={71} l={77} w={11} />
      <text x="66" y="20" fill="#ef4444" fontSize="15" textAnchor="middle">?</text>
      <Lbl x={80} y={105} text="No confirmation ? skip" color="#ef4444" />
    </svg>
  ),

  waiting: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={5}  o={52} c={45} h={30} l={74} w={11} />
      <C x={19} o={49} c={58} h={35} l={76} w={11} />
      <C x={33} o={55} c={47} h={32} l={72} w={11} />
      <text x="35" y="20" fill="#16C784" fontSize="8" textAnchor="middle">Skipped</text>
      <Lbl x={35} y={105} text="Better choice" color="#16C784" />
      <C x={108} o={52} c={45} h={30} l={74} w={11} />
      <C x={122} o={49} c={58} h={35} l={76} w={11} />
      <circle cx="138" cy="42" r="5" fill="#ef4444" opacity="0.8" />
      <C x={135} o={54} c={47} h={32} l={72} w={11} />
      <C x={149} o={50} c={65} h={62} l={69} w={11} />
      <Lbl x={148} y={105} text="Forced = loss" color="#ef4444" />
    </svg>
  ),

  risk_first: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={20} color="#16C784" dash />
      <Lbl x={7} y={17} text="Target" color="#16C784" anchor="start" />
      <HL y={55} color="#6366F1" dash />
      <Lbl x={7} y={52} text="Entry" color="#6366F1" anchor="start" />
      <HL y={82} color="#ef4444" dash />
      <Lbl x={7} y={97} text="Stop Loss" color="#ef4444" anchor="start" />
      <line x1="170" y1="20" x2="170" y2="55" stroke="#16C784" strokeWidth="2" />
      <text x="178" y="40" fill="#16C784" fontSize="8" fontFamily="sans-serif">Reward</text>
      <line x1="170" y1="55" x2="170" y2="82" stroke="#ef4444" strokeWidth="2" />
      <text x="178" y="72" fill="#ef4444" fontSize="8" fontFamily="sans-serif">Risk</text>
    </svg>
  ),

  early_entry: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={35} color="#ef4444" dash />
      <Lbl x={190} y={32} text="Resistance" color="#ef4444" anchor="end" />
      <C x={10} o={62} c={56} h={53} l={65} w={11} />
      <C x={24} o={58} c={64} h={61} l={68} w={11} />
      <circle cx="46" cy="57" r="6" fill="#ef4444" opacity="0.85" />
      <text x="50" y="48" fill="#ef4444" fontSize="8" fontFamily="sans-serif">Too early ?</text>
      <C x={42} o={62} c={57} h={54} l={65} w={11} />
      <C x={56} o={59} c={70} h={67} l={74} w={11} />
      <C x={70} o={72} c={82} h={79} l={85} w={11} />
      <C x={84} o={80} c={92} h={89} l={95} w={11} />
    </svg>
  ),

  chasing: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={45} o={88} c={15} h={10} l={91} w={32} />
      <circle cx="88" cy="18" r="6" fill="#ef4444" opacity="0.85" />
      <text x="100" y="14" fill="#ef4444" fontSize="8" fontFamily="sans-serif">Entry here ?</text>
      <text x="100" y="24" fill="#ef4444" fontSize="8" fontFamily="sans-serif">(top of move)</text>
      <C x={85}  o={18} c={34} h={16} l={36} w={12} />
      <C x={100} o={34} c={50} h={32} l={52} w={12} />
      <C x={115} o={50} c={65} h={47} l={67} w={12} />
      <Lbl x={100} y={105} text="Chasing = buying the top" color="#ef4444" />
    </svg>
  ),

  no_context: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={30} o={72} c={28} h={22} l={75} w={32} />
      <Lbl x={46} y={105} text="Looks bullish" color="#16C784" />
      <C x={108} o={28} c={38} h={25} l={40} w={9} />
      <C x={120} o={38} c={50} h={35} l={52} w={9} />
      <C x={132} o={50} c={62} h={47} l={64} w={9} />
      <C x={144} o={60} c={54} h={51} l={63} w={9} />
      <C x={156} o={56} c={68} h={64} l={71} w={9} />
      <Lbl x={145} y={105} text="It's a downtrend" color="#ef4444" />
      <Lbl x={100} y={10} text="Zoom out ? always" color="#94a3b8" />
    </svg>
  ),

  overtrading: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <polyline points="10,62 25,50 40,58 55,44 70,52 85,40"
        fill="none" stroke="#6366F1" strokeWidth="1.5" />
      {[{x:18,y:56},{x:32,y:54},{x:46,y:51},{x:60,y:48},{x:74,y:46}].map((p,i)=>(
        <circle key={i} cx={p.x} cy={p.y} r="3" fill={i%2===0?'#16C784':'#ef4444'} />
      ))}
      <Lbl x={47} y={105} text="Noise ? too many" color="#ef4444" />
      <polyline points="108,72 135,55 162,60 185,35"
        fill="none" stroke="#6366F1" strokeWidth="1.5" />
      <circle cx="122" cy="63" r="4.5" fill="#16C784" />
      <circle cx="174" cy="37" r="4.5" fill="#16C784" />
      <Lbl x={150} y={105} text="Focus ? 2 entries" color="#16C784" />
    </svg>
  ),

  decision_outcome: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <Lbl x={100} y={10} text="Process matters more than outcome" color="#94a3b8" />
      <HL y={42} x1={8} x2={90} color="#16C784" dash />
      <C x={28} o={62} c={26} h={22} l={65} w={22} />
      <text x="39" y="82" fill="#ef4444" fontSize="14" textAnchor="middle">?</text>
      <Lbl x={45} y={105} text="Good setup ? Loss" color="#ef4444" size={7} />
      <C x={128} o={57} c={52} h={33} l={62} w={16} />
      <text x="148" y="28" fill="#16C784" fontSize="14" textAnchor="middle">?</text>
      <Lbl x={148} y={105} text="Bad setup ? Win" color="#16C784" size={7} />
    </svg>
  ),

  traps_real: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <HL y={35} x1={5} x2={92} color="#ef4444" dash />
      <C x={15} o={58} c={52} h={50} l={61} w={11} />
      <C x={29} o={53} c={28} h={25} l={57} w={11} />
      <C x={43} o={33} c={50} h={30} l={54} w={11} />
      <C x={57} o={52} c={66} h={63} l={69} w={11} />
      <text x="35" y="18" fill="#ef4444" fontSize="9" textAnchor="middle">Trap ?</text>
      <HL y={35} x1={108} x2={192} color="#16C784" dash />
      <C x={115} o={60} c={54} h={51} l={63} w={11} />
      <C x={129} o={55} c={22} h={17} l={58} w={18} />
      <C x={151} o={22} c={10} h={7}  l={25} w={13} />
      <C x={167} o={10} c={5}  h={3}  l={13} w={13} />
      <text x="152" y="18" fill="#16C784" fontSize="9" textAnchor="middle">Real move ?</text>
    </svg>
  ),

  equity_curve: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Lbl x={7} y={12} text="Equity" color="#94a3b8" anchor="start" />
      <polyline points="10,90 30,82 50,76 60,82 70,70 90,62 110,57 120,63 130,50 150,42 170,36 190,28"
        fill="none" stroke="#16C784" strokeWidth="2.5" strokeLinejoin="round" />
      <Lbl x={100} y={105} text="Small consistent wins compound" color="#16C784" />
    </svg>
  ),

  forced_patient: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <Div />
      <C x={5}  o={52} c={45} h={28} l={74} w={11} />
      <C x={19} o={49} c={58} h={33} l={78} w={11} />
      <circle cx="38" cy="45" r="5" fill="#ef4444" opacity="0.85" />
      <C x={35} o={54} c={48} h={30} l={72} w={11} />
      <C x={49} o={51} c={66} h={63} l={70} w={11} />
      <text x="12" y="14" fill="#ef4444" fontSize="7" fontFamily="sans-serif">? Forced entry</text>
      <Lbl x={38} y={105} text="Loss" color="#ef4444" />
      <C x={108} o={54} c={48} h={30} l={72} w={11} />
      <C x={122} o={51} c={60} h={54} l={65} w={11} />
      <HL y={38} x1={138} x2={192} color="#16C784" dash />
      <C x={140} o={64} c={20} h={15} l={67} w={22} />
      <circle cx="162" cy="22" r="5" fill="#16C784" opacity="0.85" />
      <text x="112" y="14" fill="#16C784" fontSize="7" fontFamily="sans-serif">? Patient entry</text>
      <Lbl x={153} y={105} text="Quality" color="#16C784" />
    </svg>
  ),

  // ?? Chart Pattern visuals ??????????????????????????????????????????????
  pat_double_bottom: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={35} x1={30} x2={145} color="#ef4444" dash />
      <Lbl x={150} y={33} text="Neckline" color="#ef4444" anchor="start" size={7} />
      <polyline points="10,55 35,88 65,35 95,88 130,35 175,15"
        fill="none" stroke="#94a3b8" strokeWidth="2" />
      {[{cx:35,cy:88},{cx:95,cy:88}].map((p,i) => (
        <circle key={i} cx={p.cx} cy={p.cy} r="3.5" fill="#16C784" />
      ))}
      <text x="150" y="12" fill="#16C784" fontSize="9" fontFamily="sans-serif">? BUY</text>
      <Lbl x={65} y={105} text="W shape ? double bottom" color="#94a3b8" />
    </svg>
  ),

  pat_double_top: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={65} x1={35} x2={145} color="#16C784" dash />
      <Lbl x={150} y={63} text="Neckline" color="#16C784" anchor="start" size={7} />
      <polyline points="10,60 35,20 65,65 95,20 130,65 175,92"
        fill="none" stroke="#94a3b8" strokeWidth="2" />
      {[{cx:35,cy:20},{cx:95,cy:20}].map((p,i) => (
        <circle key={i} cx={p.cx} cy={p.cy} r="3.5" fill="#ef4444" />
      ))}
      <text x="148" y="90" fill="#ef4444" fontSize="9" fontFamily="sans-serif">? SELL</text>
      <Lbl x={65} y={105} text="M shape ? double top" color="#94a3b8" />
    </svg>
  ),

  pat_triple_bottom: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={38} x1={20} x2={150} color="#ef4444" dash />
      <Lbl x={153} y={36} text="Neck" color="#ef4444" anchor="start" size={7} />
      <polyline points="8,52 25,90 45,38 65,90 85,38 105,90 128,36 170,14"
        fill="none" stroke="#94a3b8" strokeWidth="2" />
      {[25,65,105].map((cx,i) => (
        <circle key={i} cx={cx} cy={90} r="3" fill="#16C784" />
      ))}
      <text x="148" y="12" fill="#16C784" fontSize="9" fontFamily="sans-serif">? BUY</text>
      <Lbl x={70} y={105} text="Three bottoms ? exhausted sellers" color="#94a3b8" />
    </svg>
  ),

  pat_triple_top: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={65} x1={20} x2={150} color="#16C784" dash />
      <Lbl x={153} y={63} text="Neck" color="#16C784" anchor="start" size={7} />
      <polyline points="8,62 25,18 45,65 65,18 85,65 105,18 128,68 170,92"
        fill="none" stroke="#94a3b8" strokeWidth="2" />
      {[25,65,105].map((cx,i) => (
        <circle key={i} cx={cx} cy={18} r="3" fill="#ef4444" />
      ))}
      <text x="148" y="95" fill="#ef4444" fontSize="9" fontFamily="sans-serif">? SELL</text>
      <Lbl x={70} y={105} text="Three tops ? exhausted buyers" color="#94a3b8" />
    </svg>
  ),

  pat_head_shoulders: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={58} x1={30} x2={155} color="#ef4444" dash />
      <Lbl x={157} y={56} text="Neckline" color="#ef4444" anchor="start" size={7} />
      <polyline points="8,72 28,38 50,58 75,15 100,58 122,38 148,58 170,85"
        fill="none" stroke="#94a3b8" strokeWidth="2" />
      <Lbl x={28} y={34} text="LS" color="#94a3b8" size={7} />
      <Lbl x={75} y={11} text="Head" color="#ef4444" size={7} />
      <Lbl x={122} y={34} text="RS" color="#94a3b8" size={7} />
      <text x="148" y="92" fill="#ef4444" fontSize="9" fontFamily="sans-serif">? SELL</text>
    </svg>
  ),

  pat_inv_head_shoulders: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={48} x1={30} x2={155} color="#16C784" dash />
      <Lbl x={157} y={46} text="Neckline" color="#16C784" anchor="start" size={7} />
      <polyline points="8,35 28,68 50,48 75,92 100,48 122,68 148,48 170,22"
        fill="none" stroke="#94a3b8" strokeWidth="2" />
      <Lbl x={28} y={82} text="LS" color="#94a3b8" size={7} />
      <Lbl x={75} y={105} text="Head" color="#16C784" size={7} />
      <Lbl x={122} y={82} text="RS" color="#94a3b8" size={7} />
      <text x="148" y="18" fill="#16C784" fontSize="9" fontFamily="sans-serif">? BUY</text>
    </svg>
  ),

  pat_asc_triangle: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={22} x1={10} x2={148} color="#ef4444" dash />
      <Lbl x={152} y={20} text="Resistance" color="#ef4444" anchor="start" size={7} />
      <line x1="10" y1="82" x2="135" y2="38" stroke="#16C784" strokeWidth="1.5" strokeDasharray="4,2" />
      <Lbl x={18} y={100} text="Rising support" color="#16C784" size={7} />
      <C x={10} o={75} c={65} h={62} l={80} w={12} />
      <C x={28} o={62} c={55} h={50} l={65} w={12} />
      <C x={46} o={58} c={52} h={46} l={62} w={12} />
      <C x={64} o={55} c={50} h={44} l={58} w={12} />
      <C x={84} o={52} c={48} h={42} l={56} w={12} />
      <C x={148} o={28} c={10} h={8} l={30} w={22} />
      <text x="153" y="10" fill="#16C784" fontSize="9">? Break</text>
    </svg>
  ),

  pat_desc_triangle: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={85} x1={10} x2={148} color="#16C784" dash />
      <Lbl x={152} y={83} text="Support" color="#16C784" anchor="start" size={7} />
      <line x1="10" y1="25" x2="135" y2="68" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4,2" />
      <Lbl x={18} y={20} text="Falling resistance" color="#ef4444" size={7} />
      <C x={10} o={38} c={45} h={30} l={88} w={12} />
      <C x={28} o={45} c={52} h={40} l={88} w={12} />
      <C x={46} o={52} c={60} h={48} l={88} w={12} />
      <C x={64} o={58} c={65} h={55} l={88} w={12} />
      <C x={84} o={62} c={70} h={60} l={88} w={12} />
      <C x={148} o={82} c={100} h={88} l={104} w={22} />
      <text x="152" y="105" fill="#ef4444" fontSize="9">? Break</text>
    </svg>
  ),

  pat_bull_flag: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <C x={5}  o={90} c={75} h={72} l={92} w={12} />
      <C x={20} o={75} c={58} h={55} l={77} w={12} />
      <C x={35} o={58} c={40} h={37} l={60} w={12} />
      <Lbl x={28} y={105} text="Flagpole" color="#16C784" />
      <C x={55} o={42} c={48} h={38} l={52} w={12} />
      <C x={70} o={46} c={52} h={42} l={56} w={12} />
      <C x={85} o={50} c={56} h={46} l={60} w={12} />
      <Lbl x={75} y={105} text="Flag" color="#94a3b8" />
      <HL y={38} x1={55} x2={100} color="#ef4444" dash />
      <C x={104} o={48} c={20} h={17} l={50} w={22} />
      <text x="150" y="15" fill="#16C784" fontSize="9">? Break</text>
      <Lbl x={115} y={105} text="Continuation" color="#16C784" />
    </svg>
  ),

  pat_support_bounce: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={82} color="#16C784" dash />
      <Lbl x={7} y={96} text="Support" color="#16C784" anchor="start" size={7} />
      <C x={10} o={55} c={65} h={52} l={68} w={13} />
      <C x={26} o={65} c={72} h={62} l={75} w={13} />
      <C x={42} o={72} c={78} h={68} l={88} w={13} />
      <C x={60} o={78} c={84} h={70} l={94} w={13} />
      <C x={80} o={84} c={80} h={72} l={96} w={13} />
      <circle cx="92" cy="91" r="5" fill="#16C784" opacity="0.4" />
      <C x={100} o={82} c={68} h={64} l={85} w={13} />
      <C x={116} o={68} c={55} h={52} l={70} w={13} />
      <C x={132} o={55} c={40} h={36} l={58} w={13} />
      <C x={148} o={40} c={25} h={22} l={42} w={13} />
      <text x="155" y="20" fill="#16C784" fontSize="8">? Bounce</text>
    </svg>
  ),

  pat_resistance_rejection: () => (
    <svg viewBox="0 0 200 110" className="w-full">
      <HL y={22} color="#ef4444" dash />
      <Lbl x={7} y={18} text="Resistance" color="#ef4444" anchor="start" size={7} />
      <C x={10} o={88} c={78} h={85} l={90} w={13} />
      <C x={26} o={78} c={68} h={65} l={80} w={13} />
      <C x={42} o={68} c={55} h={52} l={70} w={13} />
      <C x={60} o={55} c={48} h={45} l={58} w={13} />
      <C x={80} o={48} c={52} h={34} l={55} w={13} />
      <circle cx="87" cy="28" r="5" fill="#ef4444" opacity="0.4" />
      <C x={100} o={52} c={62} h={30} l={65} w={13} />
      <C x={116} o={62} c={72} h={58} l={75} w={13} />
      <C x={132} o={72} c={82} h={68} l={85} w={13} />
      <C x={148} o={82} c={92} h={78} l={95} w={13} />
      <text x="152" y="90" fill="#ef4444" fontSize="8">? Reject</text>
    </svg>
  ),
};

// ?? Card data (26 cards) ??????????????????????????????????????????????????

export const MODULES = [
  {
    id: 'see',
    label: 'SEE',
    icon: Eye,
    color: '#16C784',
    bgColor: '#16C78415',
    description: 'Read the chart before you trade',
    cards: [
      {
        title: 'Candle = Battle',
        explanation: 'Each candle shows who controlled price during that time ? buyers or sellers. Open, High, Low, Close tell the full story of the fight.',
        rule: 'Every candle is a battle result. Read who won.',
        visual: 'single_candle',
      },
      {
        title: 'Body vs Wick',
        explanation: 'The body shows where price spent most time ? strength. Wicks show where price tried to go but got rejected ? hesitation.',
        rule: 'Large body = conviction. Long wick = rejection.',
        visual: 'body_wick',
      },
      {
        title: 'Size Matters',
        explanation: 'Bigger candles signal strong conviction. Small alternating candles signal confusion. One large candle after small ones shows a shift in momentum.',
        rule: 'Size of candle = strength of move.',
        visual: 'size_matters',
      },
      {
        title: 'Speed of Price',
        explanation: 'Slow gradual moves show weak conviction ? easy to reverse. Fast sharp moves show strong intent and institutional participation.',
        rule: 'Fast moves are harder to fade. Slow moves are easier to reverse.',
        visual: 'speed',
      },
      {
        title: 'Trend vs Sideways',
        explanation: 'Before any trade, identify market state first. A trend has direction. Sideways moves are random ? most strategies fail in choppy markets.',
        rule: 'No trend = no trade for trend strategies.',
        visual: 'trend_sideways',
      },
      {
        title: 'Higher Highs / Lower Lows',
        explanation: 'A trend is defined by structure, not just direction. Higher Highs + Higher Lows = uptrend. Lower Highs + Lower Lows = downtrend. Structure tells you the story.',
        rule: 'Read HH/HL for uptrend. Read LH/LL for downtrend.',
        visual: 'hh_hl',
      },
      {
        title: 'Breakout',
        explanation: 'A breakout is when price moves beyond a key resistance level with momentum. The candle should be large with minimal wicks and close above the level.',
        rule: 'Breakout needs a strong close ? not just a wick poke.',
        visual: 'breakout',
      },
      {
        title: 'Rejection',
        explanation: 'When price touches a level but cannot close above it, a long wick forms. This shows supply stepped in hard. The longer the wick, the stronger the rejection.',
        rule: 'Long upper wick at resistance = sellers in control.',
        visual: 'rejection',
      },
      {
        title: 'Support & Resistance',
        explanation: 'Support and resistance are zones where price repeatedly reacts. The more times a level is tested and holds, the stronger it becomes.',
        rule: 'The more touches, the stronger the level.',
        visual: 'sr_bounces',
      },
      {
        title: 'Volume Confirmation',
        explanation: 'A breakout without volume is suspect. High volume confirms that real buyers or sellers are behind the move. Low-volume breakouts fail more often.',
        rule: 'No volume = no conviction. High volume = confirmed move.',
        visual: 'volume_confirm',
      },
      {
        title: 'Strength vs Weakness',
        explanation: 'Strong momentum shows consecutive candles in one direction with small wicks. Weak momentum is mixed candles with no follow-through.',
        rule: 'Consecutive same-color candles = momentum. Mixed = noise.',
        visual: 'strength_weakness',
      },
      {
        title: 'Pullback',
        explanation: 'Price rarely moves in a straight line. After a trend move, it pulls back before continuing. The pullback is often the best entry point.',
        rule: 'Wait for the pullback to get a better entry in the trend.',
        visual: 'pullback',
      },
      {
        title: 'Reversal',
        explanation: 'A reversal is when the current trend ends and a new direction begins. Look for rejection at a key level followed by strong opposing candles.',
        rule: 'Reversal needs rejection at a level + follow-through.',
        visual: 'reversal',
      },
      {
        title: 'Do Nothing',
        explanation: 'Messy, overlapping candles with long wicks going nowhere mean the market has no direction. Trading in this condition is gambling, not trading.',
        rule: 'Unclear market = your best trade is no trade.',
        visual: 'do_nothing',
      },
    ],
  },
  {
    id: 'think',
    label: 'THINK',
    icon: Brain,
    color: '#6366F1',
    bgColor: '#6366F115',
    description: 'Think like a disciplined trader',
    cards: [
      {
        title: 'Trend + Event + Confirmation',
        explanation: 'Good trades require three things to align: a clear trend, a meaningful event (breakout, reversal), and confirmation (volume, close). All three, every time.',
        rule: 'Missing one = weak setup. Missing two = skip it.',
        visual: 'three_checks',
      },
      {
        title: 'No Confirmation = No Trade',
        explanation: 'A candle that breaks resistance but closes back below it is not a breakout. Without confirmation, the move is likely a trap. Wait for the close.',
        rule: 'The candle must close beyond the level. A wick does not count.',
        visual: 'no_confirm',
      },
      {
        title: 'Waiting is a Decision',
        explanation: 'Skipping an unclear setup is an active trading decision. A trader who waits for quality is managing risk. A trader who forces trades is gambling.',
        rule: 'Cash is a position. Waiting is a skill.',
        visual: 'waiting',
      },
      {
        title: 'Risk First',
        explanation: 'Before thinking about profit, define how much you can lose. Set stop loss before entry. Know your risk:reward ratio. If reward is less than 2x risk, skip the trade.',
        rule: 'Define risk before reward. Always.',
        visual: 'risk_first',
      },
      {
        title: 'Entering Too Early',
        explanation: 'Entering before the setup completes ? before the breakout, before the reversal ? puts you in a position with no confirmation and maximum risk.',
        rule: 'Early entry = high risk, no confirmation. Wait for the close.',
        visual: 'early_entry',
      },
      {
        title: 'Chasing Price',
        explanation: 'Entering after a large move has already happened means you are buying at the top of momentum. The risk is high and the reward is small. Let it go.',
        rule: 'If you missed the move, wait for the next setup.',
        visual: 'chasing',
      },
      {
        title: 'No Context',
        explanation: 'A single bullish candle looks great in isolation but might be a small pullback in a strong downtrend. Always zoom out before making a decision.',
        rule: 'Check the higher timeframe before acting on any signal.',
        visual: 'no_context',
      },
      {
        title: 'Overtrading',
        explanation: 'More trades mean more commissions, more mistakes, and lower quality decisions. Professional traders are selective. Volume of trades is not a measure of skill.',
        rule: 'Fewer, better trades beat more, worse trades. Always.',
        visual: 'overtrading',
      },
      {
        title: 'Decision ? Outcome',
        explanation: 'A correct process can result in a loss. A bad decision can result in a win by luck. Over time, good decisions produce good results ? not single trades.',
        rule: 'Judge your decision by the process, not the result.',
        visual: 'decision_outcome',
      },
      {
        title: 'Traps vs Real Moves',
        explanation: 'Markets often fake a breakout to trap traders before reversing. A real breakout has volume, a clean close, and follow-through. A trap has none of these.',
        rule: 'Real move = large close + volume + continuation. Trap = wick + reverse.',
        visual: 'traps_real',
      },
      {
        title: 'Consistency > Prediction',
        explanation: 'You cannot predict markets. But you can apply the same rules every single time. Consistent behavior in uncertain markets is what builds an equity curve.',
        rule: 'You profit from process, not prediction.',
        visual: 'equity_curve',
      },
      {
        title: "Don't Force Trades",
        explanation: 'When you force a trade in a messy setup, you are betting ? not trading. Patient traders wait for clean setups with clear invalidation and good risk:reward.',
        rule: 'If you have to force it, skip it. The next setup is coming.',
        visual: 'forced_patient',
      },
    ],
  },
  {
    id: 'act',
    label: 'ACT',
    icon: Zap,
    color: '#f59e0b',
    bgColor: '#f59e0b15',
    description: 'Practice on real market setups',
    comingSoon: false,
    cards: [],
  },
  {
    id: 'chart_patterns',
    label: 'Chart Patterns',
    icon: BarChart2,
    color: '#8b5cf6',
    bgColor: '#8b5cf615',
    description: '11 patterns ? shapes, entries, stop loss and targets',
    cards: [
      {
        title: 'Double Bottom',
        explanation: 'Price hit the same support level twice ("W" shape) and failed to break lower both times. Signals buyers are defending that level. The neckline break is the entry confirmation.',
        rule: 'Entry: close above neckline. SL: below second bottom. Target: pattern height projected up.',
        visual: 'pat_double_bottom',
      },
      {
        title: 'Double Top',
        explanation: 'Price hit the same resistance level twice ("M" shape) and failed to push higher. Signals sellers are in control at that level. Classic topping pattern after an uptrend.',
        rule: 'Entry: close below neckline. SL: above second top. Target: pattern height projected down.',
        visual: 'pat_double_top',
      },
      {
        title: 'Triple Bottom',
        explanation: 'Price tested the same support three times and held each time. Stronger than a double bottom ? sellers are completely exhausted. The third test on low volume = highest confidence.',
        rule: 'Entry: neckline breakout. SL: below support. Stronger signal than double bottom.',
        visual: 'pat_triple_bottom',
      },
      {
        title: 'Triple Top',
        explanation: 'Price tested the same resistance three times and failed each time. Buyers have tried three times and cannot break through. Very high conviction reversal ? rare but reliable.',
        rule: 'Entry: close below neckline. SL: above resistance. High-reliability bearish reversal.',
        visual: 'pat_triple_top',
      },
      {
        title: 'Head & Shoulders',
        explanation: 'Three peaks ? a higher middle peak (head) flanked by two lower equal peaks (shoulders). The classic topping pattern. Volume declining on the right shoulder = sellers winning.',
        rule: 'Entry: close below neckline. SL: above right shoulder. Target: head-to-neckline depth.',
        visual: 'pat_head_shoulders',
      },
      {
        title: 'Inv. Head & Shoulders',
        explanation: 'Three troughs ? a lower middle trough (head) flanked by two higher equal troughs (shoulders). Classic bottoming pattern. Volume surge on neckline break = highest confidence.',
        rule: 'Entry: close above neckline. SL: below right shoulder. Target: head-to-neckline height.',
        visual: 'pat_inv_head_shoulders',
      },
      {
        title: 'Ascending Triangle',
        explanation: 'Flat resistance with rising support (higher lows). Buyers push harder each time they pull back. Pressure builds until price breaks above the flat ceiling. Continuation pattern in an uptrend.',
        rule: 'Entry: close above flat resistance + volume. SL: below last higher low. Target: triangle height.',
        visual: 'pat_asc_triangle',
      },
      {
        title: 'Descending Triangle',
        explanation: 'Flat support with falling resistance (lower highs). Sellers push harder each time price bounces. Pressure builds until price breaks below the floor. Continuation in a downtrend.',
        rule: 'Entry: close below flat support. SL: above last lower high. Target: triangle height.',
        visual: 'pat_desc_triangle',
      },
      {
        title: 'Bull Flag',
        explanation: 'A sharp upward move (the flagpole) followed by tight sideways or slight downward consolidation (the flag). The pause resets momentum before the next leg up. Volume dries up in the flag, surges on breakout.',
        rule: 'Entry: close above flag top. SL: below flag bottom. Target: flagpole length added to breakout.',
        visual: 'pat_bull_flag',
      },
      {
        title: 'Support Bounce',
        explanation: 'Price dipped to a known historical support level and bounced back. The level is defended again ? buyers stepped in at the same zone. More bounces from same level = stronger support.',
        rule: 'Entry: reversal candle at support + volume. SL: below support. Target: next resistance.',
        visual: 'pat_support_bounce',
      },
      {
        title: 'Resistance Rejection',
        explanation: 'Price rose to a known historical resistance level and was pushed back down. Sellers are active at this zone. Long upper wick at resistance = strong rejection signal.',
        rule: 'Entry: bearish candle at resistance (shooting star / engulfing). SL: above resistance.',
        visual: 'pat_resistance_rejection',
      },
    ],
  },
  {
    id: 'indicators',
    label: 'Scanners & Indicators',
    icon: Activity,
    color: '#06b6d4',
    bgColor: '#06b6d415',
    description: '27 scanners across 6 categories ? reference guide',
    isRef: true,
    cards: [],
  },
];

// ?? ACT Module ????????????????????????????????????????????????????????????